import api from './api';

const EPS = 0.009;

function toAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function toCLP(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}

function positiveIntOrNull(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function normalizeKey(value) {
  return String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
}

function makeUuidV4() {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;

  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Navegadores antiguos: mantiene formato UUID v4. El Edge usado por el PDV
  // dispone de crypto.randomUUID(), por lo que normalmente no se ejecutará.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : ((random & 0x3) | 0x8);
    return value.toString(16);
  });
}

export function createVentaPosClientSaleId() {
  return makeUuidV4();
}

export function buildVentaPosCartSignature({ items = [], documento = 'boleta', cliente = null, cajaId = null } = {}) {
  const compactItems = (Array.isArray(items) ? items : []).map((item) => ({
    id: item?.id ?? item?.id_producto ?? null,
    cantidad: Number(item?.cantidad || 0),
    precioBruto: Number(item?.precioBruto ?? item?.precio_bruto ?? 0),
    impuesto: Number(item?.impuestoPorcentaje ?? item?.impuesto_porcentaje ?? 0),
    productoUnidadId: item?.producto_unidad_id ?? item?.productoUnidadId ?? null,
    codigo: item?.codigoBalanza || item?.codigoBarras || item?.codigo_barras || null,
  }));

  return JSON.stringify({
    documento: documento === 'factura' ? 'factura' : 'boleta',
    customerId: positiveIntOrNull(cliente?.id ?? cliente?.id_cliente ?? cliente?.cliente_id),
    cajaId: positiveIntOrNull(cajaId),
    items: compactItems,
  });
}

function buildVentaItem(item = {}) {
  const idProducto = positiveIntOrNull(item.id ?? item.id_producto ?? item.product_id);
  const cantidad = Number(item.cantidad || 0);
  const impuestoPct = Number(item.impuestoPorcentaje ?? item.impuesto_porcentaje ?? 0) || 0;
  const precioBruto = Number(item.precioBruto ?? item.precio_bruto ?? item.precio ?? 0) || 0;

  if (!idProducto) {
    const error = new Error('Hay un producto del carrito sin id_producto válido.');
    error.code = 'POS_ITEM_PRODUCT_ID_REQUIRED';
    throw error;
  }

  if (!Number.isFinite(cantidad) || cantidad <= 0) {
    const error = new Error(`Cantidad inválida para ${item.nombre || item.sku || `producto #${idProducto}`}.`);
    error.code = 'POS_ITEM_QUANTITY_INVALID';
    throw error;
  }

  const totalBruto = toAmount(precioBruto * cantidad);
  const divisor = 1 + impuestoPct / 100;
  const totalNeto = divisor > 0 ? toAmount(totalBruto / divisor) : totalBruto;
  const totalIva = toAmount(totalBruto - totalNeto);
  const precioNeto = cantidad > 0 ? toAmount(totalNeto / cantidad) : 0;

  const productoUnidadId = positiveIntOrNull(
    item.producto_unidad_id ?? item.productoUnidadId ?? item.id_producto_unidad
  );
  const idUnidad = positiveIntOrNull(item.id_unidad ?? item.unidad_id ?? item.idUnidad);
  const factorBase = Number(item.factor_base ?? item.factorBase ?? 1);
  const safeFactor = Number.isFinite(factorBase) && factorBase > 0 ? factorBase : 1;
  const codigoVenta = firstNonEmpty(
    item.codigoBalanza,
    item.codigo_barras_venta,
    item.codigoBarras,
    item.codigo_barras,
    item.barcode,
    item.ean
  );

  const payload = {
    id_producto: idProducto,
    cantidad,
    precio_neto: precioNeto,
    impuesto_pct: impuestoPct,

    // Totales explícitos: ventas_pos los trata como la verdad final del POS y
    // los vuelve a validar antes de persistir/emitar el DTE.
    precio_bruto: toAmount(precioBruto),
    total_neto: totalNeto,
    total_iva: totalIva,
    total_bruto: totalBruto,

    sku: item.sku || null,
    nombre: item.nombre || null,
    tipo_item: item.tipo_item || (item.es_servicio ? 'SERVICIO' : 'PRODUCTO'),
    afecta_inventario: item.controla_stock !== false && item.stock_aplica !== false,
  };

  if (productoUnidadId) payload.producto_unidad_id = productoUnidadId;
  if (idUnidad) payload.id_unidad = idUnidad;
  if (item.unidad_clave || item.unidadClave) payload.unidad_clave = item.unidad_clave || item.unidadClave;
  if (item.unidad_nombre) payload.unidad_nombre = item.unidad_nombre;

  // Solo afirmamos un factor != 1 cuando el frontend también tiene una identidad
  // verificable de presentación. ventas_pos rechaza correctamente lo contrario.
  if (productoUnidadId || idUnidad || item.unidad_clave || item.unidadClave) {
    payload.factor_base = safeFactor;
    payload.cantidad_base = toAmount(cantidad * safeFactor);
  }

  if (codigoVenta) payload.codigo_barras_venta = String(codigoVenta).trim();

  return payload;
}

function sanitizePayment(payment = {}) {
  const relationId = positiveIntOrNull(
    payment.payment_relation_id ?? payment.relation_id ?? payment.relationId
  );
  const methodId = positiveIntOrNull(
    payment.payment_method_id ?? payment.metodo_pago_id ?? payment.metodopago_id ?? payment.methodId
  );
  const conditionId = positiveIntOrNull(
    payment.payment_condition_id ?? payment.condicion_pago_id ?? payment.condicionpago_id ?? payment.conditionId
  );

  const methodKey = normalizeKey(
    payment.payment_method_key ?? payment.metodo_pago_clave ?? payment.metodo?.clave
  );
  const methodName = firstNonEmpty(
    payment.payment_method_name,
    payment.metodo_pago_nombre,
    payment.metodo?.nombre,
    payment.metodo?.label,
    methodKey
  );
  const conditionKey = normalizeKey(
    payment.payment_condition_key ?? payment.condicion_pago_clave ?? payment.metodo?.condicionpago_clave
  );

  const amountApplied = toAmount(payment.amount_applied);
  const amountPaid = toAmount(firstNonEmpty(payment.amount_paid, amountApplied));
  const amountReceived = toAmount(firstNonEmpty(payment.amount_received, amountPaid));
  const changeGiven = toAmount(firstNonEmpty(payment.change_given, 0));
  const cashRounding = toAmount(firstNonEmpty(payment.cash_rounding, 0));
  const reference = firstNonEmpty(
    payment.voucher_number,
    payment.reference,
    payment.referencia
  );

  if (amountApplied <= EPS) {
    const error = new Error('Hay un tramo de pago con monto aplicado inválido.');
    error.code = 'POS_PAYMENT_AMOUNT_INVALID';
    throw error;
  }

  if (!relationId && !methodId && !methodKey) {
    const error = new Error('Hay un tramo de pago sin método identificable.');
    error.code = 'POS_PAYMENT_METHOD_REQUIRED';
    throw error;
  }

  const out = {
    amount_applied: amountApplied,
    amount_paid: amountPaid,
    amount_received: amountReceived,
    change_given: changeGiven,
    cash_rounding: cashRounding,
  };

  if (relationId) out.payment_relation_id = relationId;
  if (methodId) out.payment_method_id = methodId;
  if (methodKey) out.payment_method_key = methodKey;
  if (methodName) out.payment_method_name = String(methodName);
  if (conditionId) out.payment_condition_id = conditionId;
  if (conditionKey) out.payment_condition_key = conditionKey;

  if (reference) {
    out.voucher_number = String(reference);
    out.reference = String(reference);
    out.referencia = String(reference);
  }

  return out;
}

export function buildVentaPosPayload({
  items = [],
  pagos = [],
  documento = 'boleta',
  cliente = null,
  cajaId = null,
  clientSaleId,
  pdfFormat = '80mm',
} = {}) {
  const saleItems = (Array.isArray(items) ? items : []).map(buildVentaItem);
  const payments = (Array.isArray(pagos) ? pagos : []).map(sanitizePayment);

  if (!saleItems.length) {
    const error = new Error('La venta no contiene productos.');
    error.code = 'POS_SALE_NO_ITEMS';
    throw error;
  }

  if (!payments.length) {
    const error = new Error('La venta no contiene una forma de pago.');
    error.code = 'POS_SALE_NO_PAYMENT';
    throw error;
  }

  const totalBrutoExacto = toAmount(saleItems.reduce((sum, item) => sum + Number(item.total_bruto || 0), 0));
  const totalNeto = toAmount(saleItems.reduce((sum, item) => sum + Number(item.total_neto || 0), 0));
  const totalIva = toAmount(saleItems.reduce((sum, item) => sum + Number(item.total_iva || 0), 0));
  const totalAmount = toCLP(totalBrutoExacto);
  const totalAplicado = toAmount(payments.reduce((sum, payment) => sum + Number(payment.amount_applied || 0), 0));
  const remainingBalance = Math.max(0, toAmount(totalAmount - totalAplicado));

  const payload = {
    client_sale_id: clientSaleId || createVentaPosClientSaleId(),
    document_type: documento === 'factura' ? 'factura' : 'boleta',
    pdf_format: ['58mm', '80mm', 'carta'].includes(String(pdfFormat || '').toLowerCase())
      ? String(pdfFormat).toLowerCase()
      : '80mm',
    currency: 'CLP',
    status: remainingBalance > EPS ? 'pending' : 'completed',
    remaining_balance: remainingBalance,

    total_amount: totalAmount,
    total_bruto: totalAmount,
    total_neto: totalNeto,
    tax: totalIva,
    total_iva: totalIva,

    items: saleItems,
    payments,
  };

  const customerId = positiveIntOrNull(cliente?.id ?? cliente?.id_cliente ?? cliente?.cliente_id);
  if (customerId) payload.customer_id = customerId;

  const posCajaId = positiveIntOrNull(cajaId);
  if (posCajaId) payload.pos_caja_id = posCajaId;

  return payload;
}

export async function crearVentaPos(payload) {
  const { data } = await api.post('/ventas_pos', payload, { timeout: 90000 });
  return data;
}

export function getVentaPosPdfEndpoint(result = {}, documento = 'boleta', formato = '80mm') {
  const ventaId = positiveIntOrNull(
    result?.venta?.id_venta ??
    result?.venta?.venta_id ??
    result?.dte?.id_venta ??
    result?.dte?.venta_id
  );

  const dteUrl = firstNonEmpty(result?.dte?.pdf_url, result?.dte?.pdf_endpoint);
  let endpoint = dteUrl ? String(dteUrl) : '';

  if (!endpoint && ventaId) {
    endpoint = documento === 'factura'
      ? `/facturacion_dte33/${ventaId}/pdf`
      : `/facturacion_dte39/${ventaId}/pdf`;
  }

  if (!endpoint) return null;

  const safeFormat = ['58mm', '80mm', 'carta'].includes(String(formato || '').toLowerCase())
    ? String(formato).toLowerCase()
    : '80mm';

  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}formato=${encodeURIComponent(safeFormat)}`;
}

export async function descargarVentaPosPdf(result, {
  documento = 'boleta',
  formato = '80mm',
} = {}) {
  const endpoint = getVentaPosPdfEndpoint(result, documento, formato);

  if (!endpoint) {
    const error = new Error('La respuesta de ventas_pos no contiene una venta/DTE desde donde recuperar el PDF.');
    error.code = 'POS_DTE_PDF_ENDPOINT_NOT_AVAILABLE';
    throw error;
  }

  const { data } = await api.get(endpoint, {
    responseType: 'blob',
    timeout: 60000,
  });

  return data instanceof Blob
    ? data
    : new Blob([data], { type: 'application/pdf' });
}

export function imprimirPdfBlob(blob) {
  if (!blob) {
    return Promise.reject(new Error('PDF vacío.'));
  }

  return new Promise((resolve, reject) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    let settled = false;

    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');

    const cleanup = () => {
      try {
        iframe.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (_) {
        // Sin acción.
      }
    };

    iframe.onload = () => {
      window.setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          settled = true;
          resolve(true);
        } catch (error) {
          settled = true;
          reject(error);
        } finally {
          window.setTimeout(cleanup, 30000);
        }
      }, 250);
    };

    iframe.onerror = () => {
      if (!settled) reject(new Error('No se pudo cargar el PDF para impresión.'));
      cleanup();
    };

    iframe.src = blobUrl;
    document.body.appendChild(iframe);
  });
}

export async function imprimirDteVentaPos(result, options = {}) {
  const blob = await descargarVentaPosPdf(result, options);
  await imprimirPdfBlob(blob);
  return true;
}

export function getVentaPosError(error) {
  const data = error?.response?.data || {};
  const details = data?.details || null;
  const code = data?.error || error?.code || 'VENTA_POS_ERROR';

  let message = data?.message || error?.message || 'No se pudo registrar la venta.';

  if (code === 'VALIDATION_ERROR' && Array.isArray(details) && details.length) {
    const first = details[0];
    const path = first?.path || first?.param || '';
    const validationMessage = first?.msg || '';
    if (validationMessage) message = `${validationMessage}${path ? ` (${path})` : ''}`;
  }

  if (code === 'TOTALS_MISMATCH' && details?.expected && details?.received) {
    message = `Los totales enviados no coinciden con ventas_pos. Esperado ${details.expected.total_amount}, recibido ${details.received.total_amount}.`;
  }

  return {
    code,
    message,
    details,
    ventaId: data?.venta?.id_venta || null,
    status: error?.response?.status || null,
  };
}
